import { useEffect, useMemo, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ClientPicker — searchable client/company combobox with inline "Create new client"
 * fallback. Used in the Voice Journal review screen so reps can match their
 * spoken company names to existing CRM clients OR add a fresh one without
 * leaving the flow.
 *
 * Props:
 *   value         — currently selected client id ("" when none)
 *   onChange(id)  — called with the new client id (or "" for "no match")
 *   clients       — current client list ({id, company_name})
 *   onCreated(c)  — fired after a fresh client is created (parent should
 *                   append it to the local list and auto-select it)
 *   defaultName   — pre-fills the create dialog's company name (the AI-
 *                   transcribed name from the journal)
 *   testIdPrefix  — for E2E selectors
 */
export default function ClientPicker({
  value,
  onChange,
  clients = [],
  onCreated,
  defaultName = "",
  testIdPrefix = "client-picker",
}) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const selected = useMemo(
    () => clients.find((c) => c.id === value) || null,
    [clients, value]
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`${testIdPrefix}-trigger`}
            className="flex h-8 w-full items-center justify-between rounded-xl border border-input bg-background px-3 text-xs hover:bg-muted/40 transition tap"
          >
            <span className="flex items-center gap-1 min-w-0">
              <Building2 className="h-3 w-3 text-primary shrink-0" />
              <span className="truncate">
                {selected ? selected.company_name : "No CRM match — note only"}
              </span>
            </span>
            <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0 ml-1" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[360px] rounded-2xl" align="start">
          <Command className="rounded-2xl">
            <CommandInput placeholder="Search clients…" data-testid={`${testIdPrefix}-search`} />
            <CommandList>
              <CommandEmpty>
                <div className="text-center py-3 px-3 space-y-2">
                  <p className="text-xs text-muted-foreground">No matching client.</p>
                  <Button
                    size="sm"
                    onClick={() => { setOpen(false); setCreateOpen(true); }}
                    className="rounded-xl h-8 text-xs"
                    data-testid={`${testIdPrefix}-empty-create`}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Create new client
                  </Button>
                </div>
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__create"
                  onSelect={() => { setOpen(false); setCreateOpen(true); }}
                  className="text-xs font-semibold text-primary rounded-xl"
                  data-testid={`${testIdPrefix}-create`}
                >
                  <Plus className="h-3.5 w-3.5 mr-2" />
                  Create new client{defaultName ? ` "${defaultName}"` : ""}
                </CommandItem>
                <CommandItem
                  value="__none"
                  onSelect={() => { onChange(""); setOpen(false); }}
                  className="text-xs text-muted-foreground rounded-xl"
                  data-testid={`${testIdPrefix}-none`}
                >
                  <Check className={cn("h-3.5 w-3.5 mr-2", !value ? "opacity-100" : "opacity-0")} />
                  No CRM match — note only
                </CommandItem>
              </CommandGroup>
              {clients.length > 0 && (
                <CommandGroup heading="Existing clients">
                  {clients.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={`${c.company_name} ${c.contact_person || ""} ${c.id}`}
                      onSelect={() => { onChange(c.id); setOpen(false); }}
                      className="text-xs rounded-xl"
                      data-testid={`${testIdPrefix}-item-${c.id}`}
                    >
                      <Check className={cn("h-3.5 w-3.5 mr-2", value === c.id ? "opacity-100" : "opacity-0")} />
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{c.company_name}</p>
                        {c.contact_person && (
                          <p className="text-[10px] text-muted-foreground truncate">{c.contact_person}</p>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <QuickCreateClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultName={defaultName}
        onCreated={(c) => {
          onCreated?.(c);
          onChange(c.id);
          setCreateOpen(false);
        }}
      />
    </>
  );
}

function QuickCreateClientDialog({ open, onOpenChange, defaultName, onCreated }) {
  const [form, setForm] = useState({
    company_name: "", contact_person: "", phone: "", email: "",
    industry: "", location: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        company_name: defaultName || "", contact_person: "", phone: "",
        email: "", industry: "", location: "",
      });
    }
  }, [open, defaultName]);

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error("Company name required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", {
        company_name: form.company_name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        industry: form.industry.trim() || null,
        location: form.location.trim() || null,
        customer_type: "new_lead",
      });
      toast.success(`${data.company_name} added`);
      onCreated?.(data);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="quick-create-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">Create new client</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Just the basics for now — you can fill in GSTIN, billing details and more later from the Clients page.
          </p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Company name *</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="quick-client-name" autoFocus />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Primary contact person</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="e.g. Rajeev Kumar" data-testid="quick-client-contact" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Phone</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" data-testid="quick-client-phone" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Email</Label>
            <Input className="rounded-2xl h-10 mt-1" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quick-client-email" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Industry</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Cement" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Location</Label>
            <Input className="rounded-2xl h-10 mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Hyderabad" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="quick-client-save">
            {saving ? "Saving…" : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
